import React from "react";
import { COMPANY, DEALER, FEATURES } from "@/lib/company";
import { usePageMeta } from "@/lib/seo";
import ImpressumCards from "@/components/ImpressumCards";

const H = ({ children }) => <h2 className="font-display font-bold text-lg text-ink mt-10 mb-3">{children}</h2>;
const P = ({ children }) => <p className="text-sm text-dim leading-relaxed">{children}</p>;
const L = COMPANY.legal;

const Section = ({ title, children }) => (
  <section className="grid md:grid-cols-[220px_1fr] gap-2 md:gap-10 py-7 border-t border-line">
    <h2 className="font-display font-bold text-base text-ink">{title}</h2>
    <div className="space-y-3">{children}</div>
  </section>
);

const ext = (href, label) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="underline hover:text-ink">{label}</a>
);

const Impressum = () => (
  <>
    <p className="eyebrow">Rechtliches</p>
    <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-ink mt-3 mb-6 tracking-tight">Impressum</h1>
    {L.intro && <p className="text-base text-dim leading-relaxed max-w-2xl" data-testid="impressum-intro">{L.intro}</p>}
    <p className="text-xs text-faint mt-4 mb-10">Informationen gemäß § 5 ECG, § 14 UGB, § 63 GewO und Offenlegung gemäß § 25 MedienG</p>
    <ImpressumCards />
    <div className="mt-14">
      <Section title="Streitbeilegung">
        <P>
          Verbraucher können sich bei Streitigkeiten an die Schlichtungsstelle für Verbrauchergeschäfte
          ({ext("https://www.verbraucherschlichtung.at", "www.verbraucherschlichtung.at")}) oder an die Internet Ombudsstelle
          ({ext("https://www.ombudsstelle.at", "www.ombudsstelle.at")}) wenden. Allfällige Beschwerden können Sie auch direkt an {COMPANY.email} richten.
        </P>
      </Section>
      <Section title="Haftung für Inhalte">
        <P>
          Alle Fahrzeugangaben erfolgen nach bestem Wissen auf Basis unserer Inserate. Irrtümer, Zwischenverkauf und
          Änderungen vorbehalten. Verbindlich sind ausschließlich die Angaben im Kaufvertrag. Alle Angebote sind freibleibend und unverbindlich.
        </P>
      </Section>
      <Section title="Haftung für Links">
        <P>
          Für Inhalte externer Websites, auf die wir verlinken, sind ausschließlich deren Betreiber verantwortlich. Bei
          Bekanntwerden von Rechtsverletzungen entfernen wir entsprechende Links umgehend.
        </P>
      </Section>
      <Section title="Urheber- & Markenrechte">
        <P>
          Texte, Fotos und Grafiken dieser Website sind, sofern nicht anders angegeben, unser Eigentum bzw. nutzen wir sie
          mit Zustimmung der Rechteinhaber. Eine Verwendung ohne unsere ausdrückliche Zustimmung ist nicht gestattet.
        </P>
        <P>
          Genannte Marken- und Modellbezeichnungen sind Eigentum der jeweiligen Hersteller und dienen ausschließlich der
          Beschreibung der angebotenen Fahrzeuge. {COMPANY.legalName} steht in keiner gesellschaftsrechtlichen Verbindung zu diesen Herstellern.
        </P>
      </Section>
      {DEALER.hero.credit && (
        <Section title="Bild- und Videonachweis">
          <P>
            <span data-testid="hero-video-credit">
              Startseiten-Video: „{DEALER.hero.credit.title}“ von {DEALER.hero.credit.author},{" "}
              {ext(DEALER.hero.credit.source, "Wikimedia Commons")}, Lizenz {ext(DEALER.hero.credit.licenseUrl, DEALER.hero.credit.license)}
              {" "}– gekürzt, zugeschnitten und neu codiert.
            </span>
          </P>
        </Section>
      )}
    </div>
  </>
);

const Datenschutz = () => (
  <>
    <h1 className="font-display font-bold text-4xl sm:text-5xl text-ink mb-4 tracking-tight">Datenschutz</h1>
    <P>Wir verarbeiten Ihre personenbezogenen Daten ausschließlich auf Grundlage der DSGVO und des österreichischen DSG.</P>
    <H>1. Verantwortlicher</H>
    <P>{COMPANY.legalName}<br />{COMPANY.street}, {COMPANY.city}<br />E-Mail: {COMPANY.email}</P>
    <H>2. Anfragen über Formulare</H>
    <P>
      Wenn Sie uns über ein Formular ({FEATURES.ankauf ? "Ankauf, " : ""}{FEATURES.findet ? "Wunschfahrzeug, " : ""}Kontakt) schreiben,
      verarbeiten wir Ihre Angaben zur Bearbeitung der Anfrage (Art. 6 Abs. 1 lit. b DSGVO). Die Anfrage wird
      gespeichert und per E-Mail an {COMPANY.email} zugestellt; für den Versand nutzen wir einen E-Mail-Dienstleister
      als Auftragsverarbeiter. Die Daten werden gelöscht, sobald sie für den Zweck nicht mehr erforderlich sind und
      keine gesetzlichen Aufbewahrungspflichten bestehen.
    </P>
    <H>3. WhatsApp</H>
    <P>
      Über die WhatsApp-Schaltflächen gelangen Sie zu WhatsApp (WhatsApp Ireland Ltd.). Erst wenn Sie dort eine
      Nachricht senden, werden Daten an WhatsApp übertragen. Es gelten die Datenschutzbestimmungen von WhatsApp.
    </P>
    <H>4. Google Maps</H>
    <P>
      Zur Darstellung unseres Standorts binden wir Google Maps (Google Ireland Ltd.) ein. Beim Laden der Karte wird
      Ihre IP-Adresse an Google übertragen (Art. 6 Abs. 1 lit. f DSGVO – Darstellung des Standorts).
    </P>
    <H>5. Fahrzeugdaten</H>
    <P>Fahrzeugangaben und -fotos stammen aus unseren eigenen Inseraten bzw. unserer Fahrzeugverwaltung.</P>
    <H>6. Ihre Rechte</H>
    <P>
      Sie haben das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung, Datenübertragbarkeit und Widerspruch.
      Wenden Sie sich dazu an {COMPANY.email}. Zudem besteht ein Beschwerderecht bei der Österreichischen
      Datenschutzbehörde (www.dsb.gv.at).
    </P>
  </>
);

export default function LegalLegacy({ page }) {
  usePageMeta(page === "impressum" ? "Impressum" : "Datenschutz", null);
  return (
    <div data-testid={`legal-page-${page}`} className={`pt-28 md:pt-36 pb-24 mx-auto px-5 sm:px-8 ${page === "impressum" ? "max-w-5xl" : "max-w-3xl"}`}>
      {page === "impressum" ? <Impressum /> : <Datenschutz />}
    </div>
  );
}
